import React, { useState, useEffect, useRef } from 'react';
import authApi from '../services/auth';

export default function TwoFactorSetup({ tempToken, onSuccess }) {
  const [qrcodeDataUrl, setQrcodeDataUrl] = useState(null);
  const [secretBase32, setSecretBase32] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingQr, setLoadingQr] = useState(true);
  const [secretVisible, setSecretVisible] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    authApi.setup2fa(tempToken)
      .then(res => {
        setQrcodeDataUrl(res.data.qrcode_data_url);
        setSecretBase32(res.data.secret_base32);
        setLoadingQr(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      })
      .catch(() => {
        setError('Erreur lors de la generation du QR code. Veuillez vous reconnecter.');
        setLoadingQr(false);
      });
  }, [tempToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.replace(/\s/g, '').length !== 6) { setError('Le code doit contenir 6 chiffres'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await authApi.enable2fa(tempToken, code.replace(/\s/g, ''));
      onSuccess(res.data.user, res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'Code incorrect');
      setCode('');
      inputRef.current?.focus();
    } finally { setLoading(false); }
  };

  const formatSecret = (s) => s.match(/.{1,4}/g)?.join(' ') || s;

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: '440px' }}>
        <div className="login-icon">🛡️</div>
        <h1>Securisez votre compte</h1>
        <p className="login-subtitle">Configuration de la verification en deux etapes</p>

        <div style={{ textAlign: 'left', marginBottom: '20px' }}>
          {[
            { n: '1', text: 'Installez Microsoft Authenticator (ou Google Authenticator) sur votre telephone' },
            { n: '2', text: 'Scannez le QR code ci-dessous avec l\'application' },
            { n: '3', text: 'Entrez le code a 6 chiffres affiche pour confirmer' },
          ].map(step => (
            <div key={step.n} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
              <div style={{
                minWidth: '24px', height: '24px', borderRadius: '50%',
                background: '#3b82f6', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: '700',
              }}>{step.n}</div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569', lineHeight: '1.5', paddingTop: '2px' }}>
                {step.text}
              </p>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          {loadingQr ? (
            <div style={{ padding: '40px', color: '#94a3b8', fontSize: '0.85rem' }}>Generation du QR code...</div>
          ) : qrcodeDataUrl ? (
            <>
              <img src={qrcodeDataUrl} alt="QR code 2FA"
                style={{ width: '180px', height: '180px', border: '3px solid #e2e8f0', borderRadius: '8px' }} />
              <div style={{ marginTop: '10px' }}>
                <button type="button" onClick={() => setSecretVisible(v => !v)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}>
                  {secretVisible ? 'Masquer' : 'Saisie manuelle (impossible de scanner ?)'}
                </button>
                {secretVisible && (
                  <div style={{
                    marginTop: '8px', padding: '8px', background: '#f8fafc',
                    borderRadius: '6px', border: '1px solid #e2e8f0',
                    fontFamily: 'monospace', fontSize: '0.82rem', letterSpacing: '2px',
                  }}>
                    {formatSecret(secretBase32)}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ color: '#ef4444', fontSize: '0.85rem' }}>{error}</div>
          )}
        </div>

        {!loadingQr && qrcodeDataUrl && (
          <form onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9 ]*"
              maxLength={7}
              value={code}
              onChange={e => { setCode(e.target.value.replace(/[^0-9]/g, '')); setError(''); }}
              placeholder="000000"
              style={{
                width: '100%', padding: '14px', fontSize: '28px', fontWeight: '700',
                textAlign: 'center', letterSpacing: '10px',
                border: `2px solid ${error ? '#ef4444' : code.length === 6 ? '#10b981' : '#d1d5db'}`,
                borderRadius: '8px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px',
              }}
              autoComplete="one-time-code"
            />
            {error && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
                padding: '8px 12px', color: '#ef4444', fontSize: '0.82rem', marginBottom: '12px',
              }}>
                {error}
              </div>
            )}
            <button type="submit" disabled={loading || code.length !== 6}
              style={{
                width: '100%', padding: '12px',
                background: code.length === 6 ? '#3b82f6' : '#d1d5db',
                color: code.length === 6 ? '#fff' : '#94a3b8',
                border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '700',
                cursor: code.length === 6 ? 'pointer' : 'not-allowed',
              }}>
              {loading ? 'Verification...' : 'Activer la 2FA et se connecter'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
